"use client";

import { useMemo, useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { EllipsisVerticalIcon } from "@heroicons/react/20/solid";
import Logo from "../../Assets/IconSVG/SolidarityLogo.svg";
import { SendResetEmailTemporaryPassword, UpdateUser } from "../API/PermissionServices";
import Loader from "../../../LoadingIcons/Loader";

// ✅ Add: base URL for server images
const API_PAYMENT_BASE = process.env.REACT_APP_PAYMENT_API || "";

// --- helpers ---
function seedAccessSetsFromUser(userHubSections = []) {
  const parents = new Set();
  const children = new Set();
  for (const p of userHubSections) {
    if (p?.id) parents.add(p.id);
    if (Array.isArray(p?.children)) {
      for (const c of p.children) if (c?.id) children.add(c.id);
    }
  }
  return { parents, children };
}

export default function Drawer({
  userDetails,
  lookupSections,
  setLoading,
  fetchSolidarityUsers,
  setFormLoading,
  isFormLoading,
  toast
}) {
  const [isSaving, setIsSaving] = useState(false);
  const [open, setOpen] = useState(false);

  // Controlled top-level inputs
  const [department, setDepartment] = useState(userDetails.department ?? "");
  const [position, setPosition] = useState(userDetails.position ?? "");
  const [phoneNumber, setPhoneNumber] = useState(userDetails.phoneNumber ?? "");
  const [ext, setExt] = useState(userDetails.ext ?? "");
  const [isActive, setIsActive] = useState(userDetails.isActive);
  // Payment Requests fields
  const [requestLimit, setRequestLimit] = useState(
    userDetails.requestLimit ?? ""
  );
  const [paymentAccess, setPaymentAccess] = useState(
    userDetails.paymentRequestsAccessLevel ?? "OwnOnly"
  );
  const [paymentRequestTypes, setPaymentRequestTypes] = useState(
    Array.isArray(userDetails.requestTypes) ? userDetails.requestTypes : []
  );
  const togglePaymentRequestType = (value) => {
    setPaymentRequestTypes((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const handleReset = async (username) =>{
    setFormLoading(true);
    var res = await SendResetEmailTemporaryPassword(username);
    if(res.statusCode == 200){
      toast.success(res.message, {
          style: {
            background: "green",
            color: "white",
          },
        });
    }
    else{
      toast.error("An error occurred", {
              style: {
                background: "#ffcccc",
                color: "black",
                fontWeight: "bold",
              },
            });
    }
     
    setFormLoading(false);
  }

  // Initial access from userDetails
  const initialAccess = useMemo(
    () => seedAccessSetsFromUser(userDetails.hubSections),
    [userDetails.hubSections]
  );
  const [selectedParents, setSelectedParents] = useState(
    new Set(initialAccess.parents)
  );
  const [selectedChildren, setSelectedChildren] = useState(
    new Set(initialAccess.children)
  );

  // Confirm modals
  const [confirmParent, setConfirmParent] = useState(null);
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const [lastSavePayload, setLastSavePayload] = useState(null);

  const selectedCounts = useMemo(
    () => ({ parents: selectedParents.size, children: selectedChildren.size }),
    [selectedParents, selectedChildren]
  );

  // Find the "Payment Requests" parent id
  const paymentRequestsParentId = useMemo(() => {
    const p = lookupSections.find((s) => s.productName === "Payment Requests");
    return p?.id ?? null;
  }, [lookupSections]);

  // 👉 Popup state for settings, and helper to know if PR is selected
  const [paymentSettingsOpen, setPaymentSettingsOpen] = useState(false);
  const isPaymentRequestsSelected =
    !!paymentRequestsParentId && selectedParents.has(paymentRequestsParentId);

  // When "Payment Requests" becomes unselected, clear its fields
  useEffect(() => {
    if (!paymentRequestsParentId) return;
    const isSelected = selectedParents.has(paymentRequestsParentId);
    if (!isSelected) {
      setRequestLimit("");
      setPaymentAccess("OwnOnly");
      setPaymentRequestTypes([]);
      setPaymentSettingsOpen(false);
    }
  }, [selectedParents, paymentRequestsParentId]);

  // Cancel: reset selections back to initial & close
  const handleCancel = () => {
    setSelectedParents(new Set(initialAccess.parents));
    setSelectedChildren(new Set(initialAccess.children));
    setRequestLimit(userDetails.requestLimit ?? "");
    setPaymentAccess(userDetails.paymentRequestsAccessLevel ?? "OwnOnly");
    setPaymentRequestTypes(
      Array.isArray(userDetails.requestTypes) ? userDetails.requestTypes : []
    );
    clearAvatar(userDetails.profilePicPatth ?? "");
    setConfirmParent(null);
    setConfirmSaveOpen(false);
    setPaymentSettingsOpen(false);
    setOpen(false);
  };

  function clearAvatar(toPath = "") {
    setAvatarFile(null);
    setAvatarPreview("");
    if (fileInputRef.current) fileInputRef.current.value = "";
    setAvatarPath(toPath);
    setDragActive(false);
    dragCounterRef.current = 0;
  }

  // Toggle parent; if removing and any child selected, confirm
  const tryToggleParent = (parent) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const isSelected = selectedParents.has(parent.id);

    if (isSelected) {
      const childIds = (parent.children ?? []).map((c) => c.id);
      const hasAnySelectedChild = childIds.some((cid) =>
        selectedChildren.has(cid)
      );
      if (hasAnySelectedChild) {
        setConfirmParent(parent);
      } else {
        setSelectedParents((prev) => {
          const next = new Set(prev);
          next.delete(parent.id);
          return next;
        });
      }
    } else {
      setSelectedParents((prev) => new Set(prev).add(parent.id));
    }
  };

  // Confirm parent removal (cascade off children)
  const confirmTurnOffParent = () => {
    if (!confirmParent) return;
    setSelectedParents((prev) => {
      const next = new Set(prev);
      next.delete(confirmParent.id);
      return next;
    });
    setSelectedChildren((prev) => {
      const next = new Set(prev);
      for (const c of confirmParent.children || []) next.delete(c.id);
      return next;
    });
    setConfirmParent(null);
  };
  const cancelTurnOffParent = () => setConfirmParent(null);

  // Toggle child
  const toggleChild = (childId, parentId) => {
    setSelectedChildren((prev) => {
      const next = new Set(prev);
      const isSelected = next.has(childId);

      if (isSelected) {
        next.delete(childId);
      } else {
        next.add(childId);
        setSelectedParents((prevParents) => {
          const np = new Set(prevParents);
          np.add(parentId);
          return np;
        });
      }
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedParents(new Set());
    setSelectedChildren(new Set());
  };

  // Save flow
  const handleSaveClick = async () => setConfirmSaveOpen(true);

  const handleSaveConfirm = async () => {
    setLoading(true);
    const addedParents = [...selectedParents].filter(
      (id) => !initialAccess.parents.has(id)
    );
    const removedParents = [...initialAccess.parents].filter(
      (id) => !selectedParents.has(id)
    );
    const addedChildren = [...selectedChildren].filter(
      (id) => !initialAccess.children.has(id)
    );
    const removedChildren = [...initialAccess.children].filter(
      (id) => !selectedChildren.has(id)
    );

    let profilePicPayload = avatarPath || "";
    if (avatarFile) profilePicPayload = await fileToDataUrl(avatarFile);

    const hubSectionAdds = [...addedParents, ...addedChildren].map((id) => ({
      Id: id,
      Choice: "add",
    }));
    const hubSectionRemoves = [...removedParents, ...removedChildren].map(
      (id) => ({ Id: id, Choice: "remove" })
    );
    const hubSection = [...hubSectionAdds, ...hubSectionRemoves];

    const initialTypes = Array.isArray(userDetails.requestTypes)
      ? userDetails.requestTypes
      : [];
    const addedTypes = paymentRequestTypes.filter(
      (t) => !initialTypes.includes(t)
    );
    const removedTypes = initialTypes.filter(
      (t) => !paymentRequestTypes.includes(t)
    );
    const paymentRequestType = [
      ...addedTypes.map((t) => ({ Type: t, Choice: "add" })),
      ...removedTypes.map((t) => ({ Type: t, Choice: "remove" })),
    ];

    const apiPayload = {
      Id: userDetails.id,
      Department: department,
      Position: position,
      Token: sessionStorage.getItem("token"),
      PhoneNumber: phoneNumber,
      RequestLimit: Number(requestLimit || 0),
      PaymentRequestsAccessLevel: paymentAccess,
      Ext: ext,
      HubSection: hubSection,
      PaymentRequestType: paymentRequestType,
      ProfilePic: profilePicPayload || undefined,
      isActive: isActive
    };

    setLastSavePayload({
      user: {
        id: userDetails.id,
        client: userDetails.client,
        email: userDetails.email,
      },
      profileUpdates: {
        department,
        position,
        phoneNumber,
        ext,
        requestLimit,
        paymentRequestsAccessLevel: paymentAccess,
        paymentRequestTypes,
        profilePic: profilePicPayload,
      },
      accessDiff: {
        addedParents,
        removedParents,
        addedChildren,
        removedChildren,
      },
      timestamp: new Date().toISOString(),
    });

    await UpdateUser(apiPayload);

    setLoading(false);
    setConfirmSaveOpen(false);
    setOpen(false);
    fetchSolidarityUsers();
  };

  const handleSaveCancel = () => setConfirmSaveOpen(false);

  // ---- Avatar state ----
  const [avatarPath, setAvatarPath] = useState(
    userDetails.profilePicPatth || ""
  );
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState("");
  const [avatarError, setAvatarError] = useState("");
  const fileInputRef = useRef(null);

  const displaySrc = avatarFile
    ? avatarPreview
    : avatarPath
    ? `${API_PAYMENT_BASE}${avatarPath}`
    : "";

  // Drag & drop state
  const [dragActive, setDragActive] = useState(false);
  const dragCounterRef = useRef(0);

  // Validation
  const MAX_MB = 5;
  const ACCEPTED = [
    "image/jpeg",
    "image/png",
    "image/webp",
    "image/gif",
    "image/svg+xml",
  ];

  const handleFile = (file) => {
    setAvatarError("");
    if (!file) return;
    if (!ACCEPTED.includes(file.type)) {
      setAvatarError("Unsupported file type. Use JPG, PNG, WEBP, GIF, or SVG.");
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      setAvatarError(`Image is too large. Max ${MAX_MB}MB.`);
      return;
    }
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
    setAvatarPath("");
  };

  const onPick = (e) => handleFile(e.target.files?.[0]);
  const triggerPick = () => fileInputRef.current?.click();

  const removeAvatar = () => {
    setAvatarFile(null);
    setAvatarPreview("");
    setAvatarPath("");
    setAvatarError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    setDragActive(true);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setDragActive(false);
    }
  };
  const onDropNice = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    dragCounterRef.current = 0;
    const file = e.dataTransfer?.files?.[0];
    handleFile(file);
  };

  const fileToDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(file);
    });

  // --- Payment Requests settings (POPUP DIALOG) ---
  const limitRef = useRef(null);
  const PaymentRequestsSettings = () => (
    <Dialog
      open={paymentSettingsOpen && isPaymentRequestsSelected}
      onClose={() => setPaymentSettingsOpen(false)}
      className="relative z-[200]"
      initialFocus={limitRef} // ⬅️ keep focus on the input
    >
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel
          className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl"
          // Block bubbling so no parent toggles or weird blurs
          onMouseDownCapture={(e) => e.stopPropagation()}
          onKeyDownCapture={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between">
            <DialogTitle className="text-lg font-semibold text-gray-900">
              Payment Requests — Settings
            </DialogTitle>
            <button
              type="button"
              onClick={() => setPaymentSettingsOpen(false)}
              className="rounded-md text-gray-400 hover:text-gray-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              <XMarkIcon className="size-6" />
            </button>
          </div>

          {/* Request Limit */}
          <div className="mt-4 sm:flex sm:items-center sm:gap-6">
            <label className="block text-sm font-medium text-gray-600 sm:w-56">
              Request Limit
            </label>
            <input
              ref={limitRef}
              type="number"
              placeholder="ex. 1000"
              value={requestLimit}
              onChange={(e) => setRequestLimit(e.target.value)}
              className="mt-1 w-full rounded-md bg-white px-3 py-1.5 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus-visible:outline-2 focus-visible:outline-indigo-600 sm:mt-0"
            />
          </div>

          {/* Access Level */}
          <div className="mt-4">
            <legend className="block text-sm font-medium text-gray-600">
              Access Level
            </legend>
            <div className="mt-2 space-y-2">
              {[
                { value: "OwnOnly", label: "View Own Only" },
                { value: "AllView", label: "View All" },
                { value: "AllClaims", label: "View All Claims" },
              ].map((opt) => (
                <label
                  key={opt.value}
                  className="flex w-full items-center gap-3 rounded-md border border-gray-200 p-3 hover:bg-white cursor-pointer"
                >
                  <input
                    type="radio"
                    name="payment-access"
                    value={opt.value}
                    checked={paymentAccess === opt.value}
                    onChange={(e) => setPaymentAccess(e.target.value)}
                    className="size-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                  />
                  <span className="text-sm text-gray-900">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Types */}
          <div className="mt-4">
            <label className="mb-2 block text-sm font-medium text-gray-600">
              Request Type
            </label>
            <fieldset className="grid gap-2 sm:grid-cols-2">
              {[
                { val: "Policy", label: "Policy" },
                { val: "General Payment", label: "General Payment" },
                { val: "deductible", label: "Deductible (Claim)" },
                { val: "depreciation", label: "Depreciation (Claim)" },
                {
                  val: "ActivePolicyRecurring",
                  label: "Active Policy for recurring (Life)",
                },
              ].map(({ val, label }) => (
                <label
                  key={val}
                  className="flex items-center gap-3 rounded-md border border-gray-200 p-3 hover:bg-white"
                >
                  <input
                    type="checkbox"
                    className="size-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                    checked={paymentRequestTypes.includes(val)}
                    onChange={() => togglePaymentRequestType(val)}
                  />
                  <span className="text-sm text-gray-900">{label}</span>
                </label>
              ))}
            </fieldset>
            <p className="mt-2 text-xs text-gray-500">
              Select one or more types.
            </p>
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <button
              onClick={() => setPaymentSettingsOpen(false)}
              className="rounded-md border px-4 py-2 text-sm"
            >
              Close
            </button>
            <button
              onClick={() => setPaymentSettingsOpen(false)}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm text-white hover:bg-indigo-700"
            >
              Done
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );

  // --- Helpers to make Settings button inert inside <summary> ---
  const stopEvent = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const openPaymentSettings = (e) => {
    stopEvent(e);
    setPaymentSettingsOpen(true);
  };
  const keyOpenPaymentSettings = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      stopEvent(e);
      setPaymentSettingsOpen(true);
    }
  };

  return (
    <div>
      <button
        onClick={() => setOpen(true)}
        className="rounded-md bg-gray-950/5 px-2.5 py-1.5 text-sm font-semibold text-gray-900 hover:bg-gray-950/10"
      >
        Edit
      </button>

      <Dialog open={open} onClose={handleCancel} className="relative z-10">
        <div className="fixed inset-0" />
        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
              <DialogPanel
                transition
                className="pointer-events-auto w-screen max-w-2xl transform transition duration-500 ease-in-out data-[closed]:translate-x-full sm:duration-700"
              >
                <div className="relative flex h-full max-w-full flex-col overflow-y-auto bg-white shadow-xl">
                  <div className="px-4 py-6 sm:px-6">
                    <div className="flex items-start justify-between">
                      <DialogTitle className="text-base font-semibold text-gray-900">
                        Profile
                      </DialogTitle>
                      <div className="ml-3 flex h-7 items-center">
                        <button
                          type="button"
                          onClick={handleCancel}
                          className="relative rounded-md text-gray-400 hover:text-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                        >
                          <span className="absolute -inset-2.5" />
                          <span className="sr-only">Close panel</span>
                          <XMarkIcon aria-hidden="true" className="size-6" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Main */}
                  <div className="divide-y divide-gray-200">
                    {/* Header & actions */}
                    <div className="pb-6">
                      <div
                        className="h-24 bg-blue-200 sm:h-20 lg:h-28 bg-no-repeat bg-center bg-[length:140px]"
                        style={{ backgroundImage: `url(${Logo})` }}
                      />
                      <div className="-mt-12 flow-root px-4 sm:-mt-8 sm:flex sm:items-end sm:px-6 lg:-mt-16">
                        <div>
                          <div className="-m-1 flex">
                            <div className="inline-flex overflow-hidden rounded-lg border-4 border-white">
                              {/* Drag & drop zone */}
                              <label
                                title="Click or drag & drop an image"
                                onDragEnter={handleDragEnter}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={onDropNice}
                                className={[
                                  "relative size-24 sm:size-40 lg:size-48 flex items-center justify-center",
                                  "bg-gray-100 outline outline-1 -outline-offset-1 outline-black/5 cursor-pointer",
                                  "transition-all rounded-lg",
                                  dragActive
                                    ? "ring-2 ring-indigo-300 bg-indigo-50/70"
                                    : "",
                                ].join(" ")}
                                onClick={triggerPick}
                              >
                                {displaySrc ? (
                                  <img
                                    alt="Profile preview"
                                    src={displaySrc}
                                    className="absolute inset-0 size-full object-cover"
                                  />
                                ) : (
                                  <div className="pointer-events-none px-3 text-center text-gray-600">
                                    <div className="text-sm font-medium">
                                      Upload image
                                    </div>
                                    <div className="mt-1 text-[10px] sm:text-xs opacity-80">
                                      JPG/PNG, up to {MAX_MB}MB
                                    </div>
                                  </div>
                                )}
                                {dragActive && (
                                  <div className="pointer-events-none absolute inset-1 rounded-md border-2 border-dashed border-indigo-400/80 bg-indigo-100/30" />
                                )}
                              </label>
                            </div>
                          </div>

                          <div className="mt-2 flex flex-wrap gap-2">
                            {!displaySrc ? (
                              <button
                                type="button"
                                onClick={triggerPick}
                                className="rounded-md border px-2 py-1 text-xs"
                              >
                                Upload
                              </button>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  onClick={triggerPick}
                                  className="rounded-md border px-2 py-1 text-xs"
                                >
                                  Replace
                                </button>
                                <button
                                  type="button"
                                  onClick={removeAvatar}
                                  className="rounded-md border px-2 py-1 text-xs"
                                >
                                  Remove
                                </button>
                              </>
                            )}
                          </div>

                          {avatarError && (
                            <p className="mt-1 text-xs text-red-600">
                              {avatarError}
                            </p>
                          )}

                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={onPick}
                            className="hidden"
                          />
                        </div>

                        <div className="mt-6 sm:ml-6 sm:flex-1">
                          <div>
                            <div className="flex items-center">
                              <h3 className="text-xl font-bold text-gray-900 sm:text-2xl">
                                {userDetails.client}
                              </h3>

                              {userDetails.isActive ? (
                                <span className="ml-2.5 inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                                  <span
                                    className="inline-block size-2 shrink-0 rounded-full bg-green-400"
                                    aria-hidden="true"
                                  />
                                  Active
                                </span>
                              ) : (
                                <span className="ml-2.5 inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
                                  <span
                                    className="inline-block size-2 shrink-0 rounded-full bg-red-400"
                                    aria-hidden="true"
                                  />
                                  Disabled
                                </span>
                              )}
                            </div>

                            <p className="text-sm text-gray-500">
                              {userDetails.email}
                            </p>
                          </div>
                          <div className="mt-5 flex flex-wrap space-y-3 sm:space-x-3 sm:space-y-0">
                            <button
                            onClick={async () => await handleReset(userDetails.client)}
                              type="button"
                              className="inline-flex w-full shrink-0 items-center justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:flex-1"
                            >
                             {isFormLoading ? <Loader label="Resetting user password..." /> : 'Reset Password '} 
                            </button>
                            {/* menu trimmed */}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Fields */}
                    <div className="px-4 py-5 sm:px-0 sm:py-0">
                      <dl className="space-y-8 sm:space-y-0 sm:divide-y sm:divide-gray-200">
                        <div className="sm:flex sm:px-6 sm:py-5">
                          <dt className="text-sm font-medium text-gray-500 sm:w-40 sm:shrink-0 lg:w-48">
                            Status
                          </dt>

                          <dd className="mt-1 w-full sm:col-span-2 sm:ml-6 sm:mt-0">
                            <div className="flex items-center gap-3">
                              {/* Toggle */}
                              <label className="relative inline-flex h-7 w-14 select-none items-center">
                                <input
                                  type="checkbox"
                                  checked={isActive}
                                  onChange={(e) =>
                                    setIsActive(e.target.checked)
                                  }
                                  className="peer sr-only"
                                />
                                <span className="absolute inset-0 rounded-full bg-gray-200 ring-1 ring-inset ring-gray-300 transition-colors peer-checked:bg-emerald-600" />
                                <span className="relative ml-0.5 inline-flex h-6 w-6 translate-x-0 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-gray-200 transition-transform duration-200 peer-checked:translate-x-7" />
                                <span className="sr-only">
                                  {isActive ? "Enabled" : "Disabled"}
                                </span>
                              </label>

                              {/* Aligned text */}
                              <span
                                className={`text-sm leading-6 font-medium ${
                                  isActive
                                    ? "text-emerald-700"
                                    : "text-gray-700"
                                }`}
                              >
                                {isActive ? "Enabled" : "Disabled"}
                              </span>
                            </div>
                          </dd>
                        </div>

                        {/* Department */}
                        <div className="sm:flex sm:px-6 sm:py-5">
                          <dt className="text-sm font-medium text-gray-500 sm:w-40 sm:shrink-0 lg:w-48">
                            Department
                          </dt>
                          <dd className="mt-1 w-full text-sm text-gray-900 sm:col-span-2 sm:ml-6 sm:mt-0">
                            <input
                              type="text"
                              value={department}
                              onChange={(e) => setDepartment(e.target.value)}
                              className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600 sm:text-sm/6"
                            />
                          </dd>
                        </div>
                        {/* Position */}
                        <div className="sm:flex sm:px-6 sm:py-5">
                          <dt className="text-sm font-medium text-gray-500 sm:w-40 sm:shrink-0 lg:w-48">
                            Position
                          </dt>
                          <dd className="mt-1 w-full text-sm text-gray-900 sm:col-span-2 sm:ml-6 sm:mt-0">
                            <input
                              type="text"
                              value={position}
                              onChange={(e) => setPosition(e.target.value)}
                              className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600 sm:text-sm/6"
                            />
                          </dd>
                        </div>
                        {/* Mobile */}
                        <div className="sm:flex sm:px-6 sm:py-5">
                          <dt className="text-sm font-medium text-gray-500 sm:w-40 sm:shrink-0 lg:w-48">
                            Mobile
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:ml-6 sm:mt-0">
                            <input
                              type="text"
                              value={phoneNumber}
                              onChange={(e) => setPhoneNumber(e.target.value)}
                              className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600 sm:text-sm/6"
                            />
                          </dd>
                        </div>
                        {/* Work Phone */}
                        <div className="sm:flex sm:px-6 sm:py-5">
                          <dt className="text-sm font-medium text-gray-500 sm:w-40 sm:shrink-0 lg:w-48">
                            Work Phone
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:ml-6 sm:mt-0">
                            <input
                              type="text"
                              value={ext}
                              onChange={(e) => setExt(e.target.value)}
                              className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600 sm:text-sm/6"
                            />
                          </dd>
                        </div>

                        {/* Sections from LOOKUP */}
                        <div className="sm:flex sm:px-6 sm:py-5">
                          <dt className="text-sm font-medium text-gray-500 sm:w-40 sm:shrink-0 lg:w-48">
                            Sections
                          </dt>
                          <dd className="mt-1 w-full text-sm text-gray-900 sm:col-span-2 sm:ml-6 sm:mt-0">
                            <div className="relative h-full">
                              <div className="flex items-center justify-between p-3">
                                <div className="text-sm text-gray-600">
                                  Selected: {selectedCounts.parents} parents,{" "}
                                  {selectedCounts.children} children
                                </div>
                                <div className="flex gap-2">
                                  <button
                                    type="button"
                                    onClick={clearSelection}
                                    className="rounded-md border px-3 py-1.5 text-sm"
                                  >
                                    Clear
                                  </button>
                                </div>
                              </div>

                              <nav aria-label="Hub sections" className="h-full">
                                <ul className="divide-y divide-gray-200">
                                  {lookupSections.map((parent) => {
                                    const parentSelected = selectedParents.has(
                                      parent.id
                                    );
                                    const parentChildren =
                                      parent.children ?? [];
                                    const isPaymentRequests =
                                      parent.productName === "Payment Requests";

                                    return (
                                      <li key={parent.id} className="px-2 py-1">
                                        {parentChildren.length ? (
                                          <details className="group rounded-md">
                                            <summary
                                              className={[
                                                "flex items-center justify-between cursor-pointer list-none rounded-md px-3 py-2 hover:bg-gray-50 border",
                                                parentSelected
                                                  ? "bg-green-50 border-green-300"
                                                  : "border-transparent",
                                              ].join(" ")}
                                            >
                                              <span className="font-medium text-gray-900 break-words whitespace-normal">
                                                {parent.productName ==
                                                "AdminMain"
                                                  ? "Admin"
                                                  : parent.productName}
                                              </span>
                                              <span className="flex flex-wrap items-center gap-2">
                                                {parentSelected && (
                                                  <span className="rounded-md bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                                                    Selected
                                                  </span>
                                                )}

                                                {/* Settings button (inert to summary toggling) */}
                                                {isPaymentRequests &&
                                                  parentSelected && (
                                                    <button
                                                      type="button"
                                                      onPointerDown={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                      }}
                                                      onMouseDown={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                      }}
                                                      onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        setPaymentSettingsOpen(
                                                          true
                                                        );
                                                      }}
                                                      onKeyDown={(e) => {
                                                        if (
                                                          e.key === "Enter" ||
                                                          e.key === " "
                                                        ) {
                                                          e.preventDefault();
                                                          e.stopPropagation();
                                                          setPaymentSettingsOpen(
                                                            true
                                                          );
                                                        }
                                                      }}
                                                      className="rounded-md border px-2 py-1 text-xs text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                                                    >
                                                      Settings
                                                    </button>
                                                  )}

                                                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-sm text-gray-500">
                                                  {parentChildren.length}
                                                </span>
                                                <button
                                                  type="button"
                                                  onClick={tryToggleParent(
                                                    parent
                                                  )}
                                                  className={[
                                                    "rounded-md border px-2 py-1 text-xs",
                                                    parentSelected
                                                      ? "bg-red-600 text-white border-red-600 hover:bg-red-700"
                                                      : "text-green-700 border-green-200 hover:bg-green-50",
                                                  ].join(" ")}
                                                >
                                                  {parentSelected
                                                    ? "Remove"
                                                    : "Add"}
                                                </button>
                                                <svg
                                                  className="size-4 text-gray-500 transition-transform group-open:rotate-180"
                                                  viewBox="0 0 24 24"
                                                  fill="none"
                                                >
                                                  <path
                                                    d="M6 9l6 6 6-6"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                  />
                                                </svg>
                                              </span>
                                            </summary>

                                            {/* (No inline settings here) */}
                                            <ul className="mt-2 space-y-1 pl-6">
                                              {parentChildren.map((child) => {
                                                const isChildSelected =
                                                  selectedChildren.has(
                                                    child.id
                                                  );
                                                return (
                                                  <li
                                                    key={child.id}
                                                    className={[
                                                      "flex items-center justify-between rounded-md",
                                                      isChildSelected
                                                        ? "bg-green-50 ring-1 ring-green-300"
                                                        : "",
                                                    ].join(" ")}
                                                  >
                                                    <div className="flex-1 rounded-md px-2 py-1.5 text-sm break-words whitespace-normal">
                                                      {child.productName ==
                                                      "PaymentRequestsAdmin"
                                                        ? "User Management"
                                                        : child.productName}
                                                    </div>
                                                    <button
                                                      type="button"
                                                      onClick={() =>
                                                        toggleChild(
                                                          child.id,
                                                          parent.id
                                                        )
                                                      }
                                                      className={[
                                                        "ml-2 rounded-md border px-2 py-1 text-xs",
                                                        isChildSelected
                                                          ? "bg-red-600 text-white border-red-600 hover:bg-red-700"
                                                          : "text-green-700 border-green-200 hover:bg-green-50",
                                                      ].join(" ")}
                                                    >
                                                      {isChildSelected
                                                        ? "Remove"
                                                        : "Add"}
                                                    </button>
                                                  </li>
                                                );
                                              })}
                                            </ul>
                                          </details>
                                        ) : (
                                          <div
                                            className={[
                                              "flex items-center justify-between rounded-md px-3 py-2 border",
                                              parentSelected
                                                ? "bg-green-50 border-green-300"
                                                : "border-transparent",
                                            ].join(" ")}
                                          >
                                            <span className="font-medium text-gray-900 break-words whitespace-normal">
                                              {parent.productName}
                                            </span>
                                            <div className="flex flex-wrap items-center gap-2">
                                              {parentSelected && (
                                                <>
                                                  <span className="rounded-md bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                                                    Selected
                                                  </span>
                                                  {isPaymentRequests && (
                                                    <button
                                                      type="button"
                                                      onPointerDown={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                      }}
                                                      onMouseDown={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                      }}
                                                      onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        setPaymentSettingsOpen(
                                                          true
                                                        );
                                                      }}
                                                      onKeyDown={(e) => {
                                                        if (
                                                          e.key === "Enter" ||
                                                          e.key === " "
                                                        ) {
                                                          e.preventDefault();
                                                          e.stopPropagation();
                                                          setPaymentSettingsOpen(
                                                            true
                                                          );
                                                        }
                                                      }}
                                                      className="rounded-md border px-2 py-1 text-xs text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                                                    >
                                                      Settings
                                                    </button>
                                                  )}
                                                </>
                                              )}
                                              <button
                                                type="button"
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  tryToggleParent(parent)(e);
                                                }}
                                                className={[
                                                  "rounded-md border px-2 py-1 text-xs",
                                                  parentSelected
                                                    ? "bg-red-600 text-white border-red-600 hover:bg-red-700"
                                                    : "text-green-700 border-green-200 hover:bg-green-50",
                                                ].join(" ")}
                                              >
                                                {parentSelected
                                                  ? "Remove"
                                                  : "Add"}
                                              </button>
                                            </div>
                                          </div>
                                        )}
                                      </li>
                                    );
                                  })}
                                </ul>
                              </nav>

                              {/* Confirmation modal for turning OFF a parent with selected children */}
                              {confirmParent && (
                                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                                  <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-lg">
                                    <h2 className="text-lg font-semibold text-gray-900">
                                      Remove “{confirmParent.productName}” and
                                      all its selected sub-sections?
                                    </h2>
                                    <p className="mt-2 text-sm text-gray-600">
                                      This will remove access to the parent and
                                      any selected child items under it.
                                    </p>
                                    <div className="mt-6 flex justify-end gap-2">
                                      <button
                                        onClick={cancelTurnOffParent}
                                        className="rounded-md border px-4 py-2 text-sm"
                                      >
                                        Cancel
                                      </button>
                                      <button
                                        onClick={confirmTurnOffParent}
                                        className="rounded-md bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700"
                                      >
                                        Yes, remove access
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </dd>
                        </div>

                        {/* Created Date */}
                        <div className="sm:flex sm:px-6 sm:py-5">
                          <dt className="text-sm font-medium text-gray-500 sm:w-40 sm:shrink-0 lg:w-48">
                            Created Date
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:ml-6 sm:mt-0">
                            <time dateTime={userDetails?.createdDate}>
                              {userDetails?.createdDate
                                ? new Date(
                                    userDetails.createdDate
                                  ).toLocaleString("en-GB", {
                                    timeZone: "Asia/Bahrain",
                                    weekday: "short",
                                    year: "numeric",
                                    month: "short",
                                    day: "2-digit",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    second: "2-digit",
                                    hour12: true,
                                  })
                                : "—"}
                            </time>
                          </dd>
                        </div>
                      </dl>
                    </div>

                    {/* Footer */}
                    <div className="shrink-0 border-t border-gray-200 px-4 py-5 sm:px-6">
                      <div className="flex justify-end space-x-3">
                        <button
                          type="button"
                          onClick={handleCancel}
                          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleSaveClick}
                          className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </DialogPanel>
            </div>
          </div>
        </div>
      </Dialog>

      {/* Save confirmation popup */}
      <Dialog
        open={confirmSaveOpen}
        onClose={handleSaveCancel}
        className="relative z-[200]"
      >
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <DialogTitle className="text-lg font-semibold text-gray-900">
              Are you sure you want to save?
            </DialogTitle>
            <p className="mt-2 text-sm text-gray-600">
              This will save profile changes and the current access selection.
            </p>

            <div className="mt-4 grid grid-cols-2 gap-3 text-xs text-gray-600">
              <div>
                <span className="font-medium text-gray-800">Department:</span>{" "}
                {department || "—"}
              </div>
              <div>
                <span className="font-medium text-gray-800">Position:</span>{" "}
                {position || "—"}
              </div>
              <div>
                <span className="font-medium text-gray-800">Mobile:</span>{" "}
                {phoneNumber || "—"}
              </div>
              <div>
                <span className="font-medium text-gray-800">Work Phone:</span>{" "}
                {ext || "—"}
              </div>
              <div>
                <span className="font-medium text-gray-800">Limit:</span>{" "}
                {requestLimit || "—"}
              </div>
              <div>
                <span className="font-medium text-gray-800">Access:</span>{" "}
                {paymentAccess}
              </div>
              <div className="col-span-2">
                <span className="font-medium text-gray-800">Types:</span>{" "}
                {paymentRequestTypes.length
                  ? paymentRequestTypes.join(", ")
                  : "—"}
              </div>
              <div className="col-span-2">
                <span className="font-medium text-gray-800">Selected:</span>{" "}
                {selectedCounts.parents} parent(s), {selectedCounts.children}{" "}
                child(ren)
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={handleSaveCancel}
                disabled={isSaving}
                className="rounded-md border px-4 py-2 text-sm disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveConfirm}
                disabled={isSaving}
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {isSaving ? "Saving..." : "Yes, Save"}
              </button>
            </div>
          </DialogPanel>
        </div>
        
      </Dialog>

      {/* Payment Requests Settings Popup */}
      
      <PaymentRequestsSettings />
      
    </div>
  );
}
